import * as Sentry from "@sentry/browser";

const DENY_URLS = [
    /localhost/,

    // Google Tag Manager / Analytics
    /googletagmanager\.com/i,
    /google-analytics\.com/i,

    // Facebook / Meta Pixel
    /connect\.facebook\.net/i,

    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,

    // Safari extensions
    /safari-extension:\/\//i,

    // Smartsupp
    /smartsuppchat\.com/i,
    /bootstrap\.smartsuppchat\.com/i,
    /v3\.smartsuppcdn\.com/i,
    /widget-v3\.smartsuppcdn\.com/i,

    // tawk.to
    /embed\.tawk\.to/i,
    /va\.tawk\.to/i,
    /tawk\.to/i,
    /twk-chunk/i,

    // Pinterest
    /ct\.pinterest\.com/i,
    /s\.pinimg\.com/i,

    // LinkedIn
    /px\.ads\.linkedin\.com/i,

    // PPL / DHL widget
    /api\.dhl\.com/i,

    // Facebook / Meta in-app browser injected instrumentation (iabjs://)
    /^iabjs:\/\//i
];

const IGNORE_ERRORS = [
    /could not be loaded[\s\S]*map\.addImage/i,
    /Invalid snap index/i,
    /\[Meta Pixel\]/i,
    /window\.webkit\.messageHandlers/i, // iOS WKWebView bridge
    /Java object is gone/i, // Android WebView bridge
    /(Can't find variable: fbq|fbq is not defined)/i, // Meta Pixel fired via GTM before base code loaded
    /getComputedStyle must be an instance of Element|'getComputedStyle' on 'Window': parameter 1 is not of type 'Element'/i, // simplelightbox touch on image that isn't loaded yet (Safari | Chrome)
    /Cannot redefine property: browserAction/i, // browser extension injecting into the page
    /Cannot read properties of (?:null|undefined) \(reading 'touches'\)/i, // touch handler from browser extension / in-app browser
    /\[Tawk\/Widget-Settings\]/i, // tawk.to chat widget console noise
    /google is not defined/i, // Google Maps script blocked (adblock / network)
    /NotLoadingAPIFromGoogleMapsError/i, // Google Maps rušené rozšířením/proxy (re-serve mimo googleapis.com)
    /Error invoking postMessage[\s\S]*Java exception was raised/i, // Facebook in-app browser native bridge
    /currentImage['")\]]*\.getAttribute|currentImage is null/i, // simplelightbox async load race po zavření
    /Fullscreen is not supported/i,
    /The play\(\) request was interrupted/i,
    /PlayInterrupted/i,
    /undefined is not an object \(evaluating '[^']*\.touches'\)/i,
];

const TRANSIENT_NETWORK_ERROR = /failed to fetch|load failed|networkerror|network request failed|the network connection was lost/i;

export default class SentryErrorHandler {
    /**
     * @param {string} dsn
     * @param {{denyUrls?: Array<RegExp|string>, ignoreErrors?: Array<RegExp|string>}} options project-specific patterns, merged with the common ones
     */
    static init(dsn, { denyUrls = [], ignoreErrors = [] } = {}) {
        const allDenyUrls = [...DENY_URLS, ...denyUrls];

        Sentry.init({
            dsn: dsn,
            integrations: [
                Sentry.captureConsoleIntegration({
                    levels: ['error']
                }),
                Sentry.httpClientIntegration()
            ],
            ignoreErrors: [...IGNORE_ERRORS, ...ignoreErrors],
            denyUrls: allDenyUrls,
            beforeSend(event, hint) {
                const reqUrl = event.request?.url ?? '';

                if (reqUrl && allDenyUrls.some((re) => (re instanceof RegExp ? re.test(reqUrl) : reqUrl.includes(re)))) {
                    return null;
                }

                const error = hint?.originalException;

                if (error?.name === 'AbortError') {
                    return null;
                }

                const message = error?.message ?? event.message ?? '';

                if (TRANSIENT_NETWORK_ERROR.test(message)) {
                    return null;
                }

                const ex = event.exception?.values?.[0];

                if (ex && !ex.type && !ex.value && !ex.stacktrace) {
                    return null;
                }

                return event;
            },
        });
    }
}